import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Check, XCircle, Loader2, Lock } from 'lucide-react';
import { Card, CardHeader, CardContent } from '../../components/ui/card';
import PageTitle from '../../components/generics/page-title';
import CollaboratorSelect from '../../components/dashboard/collaborator-select';
import { bookingStore } from '../../store/booking-store';
import { bookingViewModelMobX } from '../../view-model/professional/booking-view-model';
import { useCalendarViewModel } from '../../view-model/professional/calendar-view-model';
import ReservationTable from '../../components/reservations/reservation-table';
import ReservationGrid from '../../components/reservations/reservation-grid';
import ReservationDetails from '../../components/reservations/reservation-details';
import ReservationFilters from '../../components/reservations/reservation-filters';
import ReservationStats from '../../components/reservations/reservation-stats';
import ExportRevenueModal from '../../components/reservations/export-revenue-modal';
import EditManualBookingModal from '../../components/calendar/edit-manual-booking-modal';
import { BookingDetail, BookingStatus } from '../../model/professional/booking-model';
import { t } from 'i18next';
import OnboardingTour from '../onboarding/onboarding-tour';
import { SECTION_RESERVATION } from '../../constants/onboarding-sections';
import ReviewTutorialButton from '../../components/onboarding/review-tutorial-button';
import companyStore from '../../store/company-store';
import professionalStore from '../../store/professional-store';
import subscriptionStore from '../../store/subscription-store';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Reservations: React.FC = observer(() => {
  const viewModel = bookingViewModelMobX;
  const calendarViewModel = useCalendarViewModel();
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const navigate = useNavigate();
  const { t: tHook, i18n } = useTranslation();
  const currentLang = i18n.language || 'fr';

  // Vérifier si l'utilisateur a accès à cette fonctionnalité
  const hasAdvancedAnalytics = subscriptionStore.currentSubscription?.advancedAnalytics ?? false;

  // Charger la compagnie sélectionnée pour le select des collaborateurs et initialiser le collaborateur connecté
  useEffect(() => {
    const companyId = companyStore.getIdCompanySelectedDefault;
    const currentUserId = professionalStore.getId;

    if (!companyId || !currentUserId) {
      return;
    }

    companyStore.setCompanyById(companyId);

    // Sélectionner le collaborateur connecté par défaut
    if (companyStore.selectedCompany?.collaboratorCompanies) {
      const currentCollaborator = companyStore.selectedCompany.collaboratorCompanies.find(
        c => c.collaboratorId === currentUserId
      );
      if (currentCollaborator) {
        viewModel.setSelectedCollaboratorId(currentCollaborator.collaboratorCompagnieId);
      }
    }
  }, [companyStore.selectedCompany, professionalStore.getId, viewModel]);

  const handleCollaboratorChange = (collaboratorId: string) => {
    viewModel.setSelectedCollaboratorId(collaboratorId);
  };

  // Calculer les statistiques en utilisant le ViewModel
  const stats = viewModel.calculateStats(bookingStore.bookings);

  // Wrapper pour ajouter les icônes React aux informations de statut
  const getStatusInfo = (status: BookingStatus | 'canceled') => {
    const statusInfo = viewModel.getStatusInfo(status);

    // Ajouter l'icône React basée sur le type d'icône
    let icon = null;
    if (statusInfo.iconType === 'check') {
      icon = <Check size={14} />;
    } else if (statusInfo.iconType === 'xCircle') {
      icon = <XCircle size={14} />;
    }

    return {
      ...statusInfo,
      icon,
      label: t(`reservations.${status}`, statusInfo.label)
    };
  };

  // Gérer l'export
  const handleExport = async (
    startDate: string,
    endDate: string,
    exportFormat: 'csv' | 'pdf',
    collaboratorId?: string
  ): Promise<boolean> => {
    const companyId = companyStore.getIdCompanySelectedDefault;

    if (!companyId) {
      return false;
    }

    return await viewModel.exportRevenue(
      startDate,
      endDate,
      companyId,
      exportFormat,
      collaboratorId
    );
  };

  // Obtenir les dates par défaut depuis le filtre actuel
  const getDefaultDates = () => {
    const dateFilter = viewModel.getDateRangeFilter();
    return {
      startDate: dateFilter.startDate,
      endDate: dateFilter.endDate
    };
  };

  // Si l'utilisateur n'a pas accès à cette fonctionnalité, afficher un message de blocage
  if (!hasAdvancedAnalytics) {
    return (
      <div className="h-[calc(100vh-2rem)] flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-amber-600 dark:text-amber-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            {tHook('subscription.featureLocked', 'Fonctionnalité Premium')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {tHook('subscription.reservationsLockedMessage', 'L\'accès aux réservations avancées nécessite un abonnement Premium ou Premium+.')}
          </p>
          <button
            onClick={() => navigate(`/${currentLang}/provider/subscription`)}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium"
          >
            {tHook('subscription.upgrade', 'Mettre à niveau')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    <OnboardingTour section={SECTION_RESERVATION} />
    <div className="h-[calc(100vh-2rem)] flex flex-col space-y-3">
      <PageTitle
        title={t('reservations.title', 'Réservations')}
        subtitle={t('reservations.subtitle', 'Gérez vos rendez-vous avec vos clients')}
      />

      {/* Collaborator Select */}
      <div className="flex-none w-full md:w-80">
        <CollaboratorSelect
          value={viewModel.selectedCollaboratorId}
          onChange={handleCollaboratorChange}
        />
      </div>

      <div className="fixed bottom-6 z-50">
        <ReviewTutorialButton section={SECTION_RESERVATION}/>
      </div>

      {/* Quick Stats */}
      <ReservationStats stats={stats} />
      
      <Card className="flex-1 flex flex-col min-h-0">
        <CardHeader className="flex-none space-y-4">
          {/* Filters */}
          <ReservationFilters
            activeFilter={viewModel.activeFilter}
            setActiveFilter={(filter: any) => viewModel.setActiveFilter(filter)}
            searchTerm={viewModel.searchTerm}
            setSearchTerm={(term) => viewModel.setSearchTerm(term)}
            dateRangeMode={viewModel.dateRangeMode}
            selectedDate={viewModel.selectedDate}
            setDateRangeMode={(mode) => viewModel.setDateRangeMode(mode)}
            setSelectedDate={(date) => viewModel.setSelectedDate(date)}
            viewMode={viewModel.viewMode}
            setViewMode={(mode) => viewModel.setViewMode(mode)}
            onExportClick={() => {
              setIsExportModalOpen(true)
              companyStore.setCompanyById(companyStore?.getIdCompanySelectedDefault)
            }}
          />
        </CardHeader>
        
        <CardContent className="flex-1 overflow-y-auto reservation-view">
          {viewModel.viewMode === 'list' ? (
            <ReservationTable
              reservations={bookingStore.bookings}
              selectedReservations={viewModel.selectedReservations}
              onToggleReservation={(id) => viewModel.toggleReservation(id)}
              onShowDetails={(id) => viewModel.setShowDetailsId(id)}
              getStatusInfo={getStatusInfo}
              formatDate={(date) => viewModel.formatDate(date)}
              formatTime={(time) => viewModel.formatTime(time)}
            />
          ) : (
            <ReservationGrid
              reservations={bookingStore.bookings}
              onShowDetails={(id) => viewModel.setShowDetailsId(id)}
              getStatusInfo={getStatusInfo}
              formatDate={(date) => viewModel.formatDate(date)}
              formatTime={(time) => viewModel.formatTime(time)}
            />
          )}
        </CardContent>
      </Card>

      {/* Reservation Details Modal */}
      {viewModel.showDetailsId !== null && (
        <ReservationDetailsLoader
          reservationId={viewModel.showDetailsId}
          onClose={() => viewModel.setShowDetailsId(null)}
          onEdit={(booking) => {
            calendarViewModel.openEditManualBookingModal(booking);
            viewModel.setShowDetailsId(null);
          }}
          getStatusInfo={getStatusInfo}
          getReservationDetails={(id) => viewModel.getReservationDetails(id)}
        />
      )}

      {/* Export Revenue Modal */}
      <ExportRevenueModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExport}
        defaultStartDate={getDefaultDates().startDate}
        defaultEndDate={getDefaultDates().endDate}
      />

      {/* Modal d'édition de réservation manuelle */}
      <EditManualBookingModal />
    </div>
    </>
  );
});

// Composant pour charger les détails d'une réservation de manière asynchrone
interface ReservationDetailsLoaderProps {
  reservationId: string;
  onClose: () => void;
  onEdit?: (booking: BookingDetail) => void;
  getStatusInfo: (status: BookingStatus | 'canceled') => any;
  getReservationDetails: (id: string) => Promise<BookingDetail | null>;
}

const ReservationDetailsLoader: React.FC<ReservationDetailsLoaderProps> = ({
  reservationId,
  onClose,
  onEdit,
  getStatusInfo,
  getReservationDetails
}) => {
  const [reservation, setReservation] = useState<BookingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const loadReservationDetails = async () => {
      setLoading(true);
      try {
        const details = await getReservationDetails(reservationId);
        setReservation(details);
      } catch (error) {
        console.error('Erreur lors du chargement des détails de la réservation:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadReservationDetails().then();
  }, [reservationId, getReservationDetails]);
  
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-8 flex flex-col items-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-gray-600 dark:text-gray-400">{t('reservations.loadingDetails', 'Chargement des détails...')}</p>
        </div>
      </div>
    );
  }
  
  if (!reservation) {
    return null;
  }
  
  return (
    <ReservationDetails
      reservation={reservation}
      onClose={onClose}
      onEdit={onEdit}
      getStatusInfo={getStatusInfo}
    />
  );
};

export default Reservations;
